import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Descriptions,
  Spin,
  Input,
  Collapse,
  Tag,
  Modal,
  Form,
  Select,
  DatePicker,
  App,
  Dropdown,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import { PrintableVoucher } from '@/components/sales-invoice/PrintableVoucher';
import { useUsers } from '@/hooks/useUsers';
import {
  useDelivery,
  useUpdateDeliveryItemPacking,
  useUpdateDeliveryCountryOfOrigin,
  useUpdateDeliveryWarehouse,
  useReplaceDeliveryPackages,
  useMarkDeliveryInTransit,
  useMarkDeliveryReturned,
  useUpdateDeliveryTransitDetails,
} from '@/hooks/useDelivery';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getDeliveryStatusTagColor } from '@/services/delivery.service';
import type { DeliveryPackageInput, DeliveryPackageType } from '@/services/delivery.service';
import type { MenuProps } from 'antd';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useAuthStore } from '@/store/authStore';
import DeliveryCompletionModal from '@/components/sales/DeliveryCompletionModal';
import ProofOfDeliveryUpload from '@/components/sales/ProofOfDeliveryUpload';
import InspectionReleaseNoteComponent from '@/components/qc/InspectionReleaseNote';
import { useDocumentsByEntity } from '@/hooks/useDocuments';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import {
  useCreateDocumentCollectionForDelivery,
  useDeliveryDocumentPackage,
} from '@/hooks/useDocumentCollection';
import type { Document } from '@/services/document.service';
import { documentService } from '@/services/document.service';
import warehouseService from '@/services/warehouse.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Text } = Typography;

const DELIVERY_REPORT_VARIANTS = [
  { value: 'delivery-note', label: 'Delivery note' },
  { value: 'delivery-packing-list', label: 'Packing list' },
  { value: 'delivery-commercial-invoice', label: 'Commercial invoice' },
];

const DELIVERY_TRANSITION_PERMS = ['growth.delivery.transition', 'growth.delivery.edit', 'growth.order.edit', 'growth.view'] as const;

function userCanTransitionDelivery(user: { is_super_admin?: boolean; permissions?: string[] } | null | undefined): boolean {
  if (!user) return false;
  if (user.is_super_admin) return true;
  const p = user.permissions ?? [];
  return DELIVERY_TRANSITION_PERMS.some((code) => p.includes(code));
}

const DeliveryDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [stageModal, setStageModal] = useState<'in-transit' | 'delivered' | 'returned' | null>(null);
  const [transitEditOpen, setTransitEditOpen] = useState(false);
  const [stageForm] = Form.useForm<{
    remarks?: string;
    tracking_number?: string;
    shipping_method?: string;
    vehicle_number?: string;
    driver_name?: string;
    delivery_assignee_user_id?: string;
  }>();
  const [transitForm] = Form.useForm<{
    remarks?: string;
    tracking_number?: string;
    shipping_method?: string;
    vehicle_number?: string;
    driver_name?: string;
    delivery_assignee_user_id?: string;
    in_transit_at?: dayjs.Dayjs | null;
  }>();
  const { data: delivery, isLoading } = useDelivery(id);
  const { data: approvedPackage = [] } = useDeliveryDocumentPackage(id);
  const createDocumentCollection = useCreateDocumentCollectionForDelivery(id);
  const { data: revenueServiceTypesDn = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray((body as { data?: unknown })?.data) ? (body as { data: { id: string; service_code: string; service_name: string }[] }).data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const dnRstLabel = useMemo(() => {
    const rid = delivery?.revenue_service_type_id;
    if (!rid) return '—';
    const r = revenueServiceTypesDn.find((x) => x.id === rid);
    return r ? `${r.service_code} — ${r.service_name}` : '—';
  }, [delivery?.revenue_service_type_id, revenueServiceTypesDn]);
  const { data: assigneeUsers = [], isLoading: assigneeUsersLoading } = useUsers({
    is_active: true,
  });
  const { data: deliveryDocs = [] } = useDocumentsByEntity('DeliveryNote', id || '');
  const updatePacking = useUpdateDeliveryItemPacking(id);
  const updateCountryOfOrigin = useUpdateDeliveryCountryOfOrigin(id);
  const updateWarehouse = useUpdateDeliveryWarehouse(id);
  const replacePackages = useReplaceDeliveryPackages(id);
  const markInTransit = useMarkDeliveryInTransit();
  const markReturned = useMarkDeliveryReturned();
  const updateTransit = useUpdateDeliveryTransitDetails();
  const canTransition = userCanTransitionDelivery(user);
  const transitionBusy = markInTransit.isPending || markReturned.isPending || updateTransit.isPending;
  const [printingVoucher, setPrintingVoucher] = useState(false);
  const [reportKey, setReportKey] = useState(DELIVERY_REPORT_VARIANTS[0].value);
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses', 'dn-detail'],
    queryFn: () => warehouseService.getAll(),
  });
  const warehouseLabel = useMemo(() => {
    const wid = delivery?.warehouse_id;
    if (!wid) return '—';
    const w = warehouses.find((x) => x.id === wid);
    return w ? `${w.warehouse_code} — ${w.warehouse_name}` : wid;
  }, [delivery?.warehouse_id, warehouses]);

  type PackageDraftRow = DeliveryPackageInput & { key: string };
  const [packageDrafts, setPackageDrafts] = useState<PackageDraftRow[]>([]);

  useEffect(() => {
    const pkgs = delivery?.packages || [];
    setPackageDrafts(
      pkgs.map((p, i) => ({
        key: p.id || `pkg-${i}`,
        package_type: (p.package_type as DeliveryPackageType) || 'box',
        quantity: Number(p.quantity) || 1,
        size: p.size ?? '',
        weight: p.weight != null ? Number(p.weight) : undefined,
        weight_uom: p.weight_uom || 'KG',
        remarks: p.remarks ?? '',
      }))
    );
  }, [delivery?.id, delivery?.packages, delivery?.updated_at]);

  const breadcrumbSegments = useMemo(
    () =>
      delivery
        ? [
            { title: 'Growth & Revenue', path: '/growth/dashboard' },
            { title: 'Delivery notes', path: '/growth/delivery-notes' },
            { title: delivery.delivery_note_number || 'DN' },
          ]
        : null,
    [delivery?.id, delivery?.delivery_note_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  useEffect(() => {
    if (searchParams.get('stage') !== 'in-transit' || !delivery) return;
    const next = new URLSearchParams(searchParams);
    next.delete('stage');
    setSearchParams(next, { replace: true });
    if (delivery.delivery_status === 'Prepared') {
      setStageModal('in-transit');
    }
  }, [delivery?.id, delivery?.delivery_status, searchParams, setSearchParams]);

  useEffect(() => {
    if (stageModal && delivery) {
      stageForm.setFieldsValue({
        remarks: delivery.status_transition_remarks ?? '',
        tracking_number: delivery.tracking_number ?? '',
        shipping_method: delivery.shipping_method ?? '',
        vehicle_number: delivery.vehicle_number ?? '',
        driver_name: delivery.driver_name ?? '',
        delivery_assignee_user_id: delivery.delivery_assignee_user_id ?? undefined,
      });
    }
  }, [
    stageModal,
    delivery?.id,
    delivery?.status_transition_remarks,
    delivery?.tracking_number,
    delivery?.shipping_method,
    delivery?.vehicle_number,
    delivery?.driver_name,
    delivery?.delivery_assignee_user_id,
    stageForm,
  ]);

  const assigneeUserOptions = useMemo(
    () =>
      assigneeUsers.map((u) => ({
        value: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username,
      })),
    [assigneeUsers]
  );
  const preparedByUsername = useMemo(() => {
    const uid = delivery?.created_by;
    if (!uid) return '-';
    const u = assigneeUsers.find((x) => x.id === uid);
    return u?.username || [u?.first_name, u?.last_name].filter(Boolean).join(' ') || '-';
  }, [delivery?.created_by, assigneeUsers]);
  const proofDocs = useMemo(
    () =>
      deliveryDocs.filter(
        (d: Document) => String(d.document_category || '').toLowerCase() === 'proofofdelivery'
      ),
    [deliveryDocs]
  );

  const dnWorkflowStages = useMemo(() => {
    if (!delivery) return [];
    const st = delivery.delivery_status || 'Prepared';
    const delivered = st === 'Delivered';
    const transit = st === 'In Transit';
    const returned = st === 'Returned';
    if (returned) {
      return [
        {
          title: 'Prepared',
          description: 'Ready to ship',
          status: 'finish' as const,
        },
        {
          title: 'In transit',
          description: 'Shipping',
          status: 'finish' as const,
        },
        {
          title: 'Returned',
          description: 'Not accepted / returned',
          status: 'error' as const,
        },
      ];
    }
    return [
      {
        title: 'Prepared',
        description: 'Ready to ship',
        status: (delivered || transit ? 'finish' : 'process') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'In transit',
        description: 'Shipping',
        status: (delivered ? 'finish' : transit ? 'process' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: 'Delivered',
        description: 'Complete',
        status: (delivered ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [delivery]);

  const dnWorkflowCurrent = useMemo(() => {
    if (!delivery) return 0;
    const st = delivery.delivery_status || 'Prepared';
    if (st === 'Returned') return 2;
    if (st === 'Delivered') return 2;
    if (st === 'In Transit') return 1;
    return 0;
  }, [delivery]);

  if (!id) {
    navigate('/growth/delivery-notes');
    return null;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!delivery) {
    return (
      <Card>
        <Text type="secondary">Delivery not found</Text>
      </Card>
    );
  }

  const handlePrintStockVoucher = async () => {
    const voucherId = delivery.stock_journal_voucher_id;
    if (!voucherId) {
      message.warning('No accounting voucher linked. Stock/COGS must be posted first.');
      return;
    }
    try {
      setPrintingVoucher(true);
      const voucherRes = await api.get(`/finance/vouchers/${voucherId}`);
      const voucher = (voucherRes as { data?: { data?: unknown } })?.data?.data ?? voucherRes?.data;
      if (!voucher || !(voucher as { id?: string }).id) {
        message.warning('Accounting voucher not found.');
        return;
      }
      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.display = 'block';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(
        <PrintableVoucher
          voucher={voucher as never}
          referenceLabel="Delivery Note"
          onClose={cleanup}
        />
      );
      window.addEventListener('afterprint', cleanup);
      setTimeout(() => window.print(), 300);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Failed to print voucher');
    } finally {
      setPrintingVoucher(false);
    }
  };

  type DeliveryNoteItemShape = {
    id: string;
    line_number: number;
    item_description?: string;
    qty_required?: number;
    delivered_quantity: number;
    weight?: number;
    hsn_code?: string;
  };

  const items: DeliveryNoteItemShape[] = (delivery.items || []).map((it) => ({
    id: it.id,
    line_number: it.line_number,
    item_description: it.item_description,
    qty_required: (it as any).salesOrderItem?.ordered_quantity,
    delivered_quantity: it.delivered_quantity,
    weight: it.weight,
    hsn_code: it.hsn_code,
  }));

  const packingLocked =
    delivery.delivery_status === 'Delivered' || delivery.delivery_status === 'Returned';

  const columns: ColumnsType<DeliveryNoteItemShape> = [
    { title: 'PO Sr', dataIndex: 'line_number', key: 'line_number', width: 70 },
    { title: 'Specification', dataIndex: 'item_description', key: 'spec', ellipsis: true },
    {
      title: 'Revision',
      dataIndex: 'revision',
      key: 'revision',
      width: 80,
      render: (v: string) => v || '—',
    },
    { title: 'Qty Required', dataIndex: 'qty_required', key: 'qty_required', width: 100, align: 'right' },
    { title: 'Qty Delivered', dataIndex: 'delivered_quantity', key: 'qty_delivered', width: 110, align: 'right' },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (val: number | undefined, record: DeliveryNoteItemShape) =>
        packingLocked ? (
          <span>{val != null ? String(val) : '—'}</span>
        ) : (
          <Input
            type="number"
            size="small"
            min={0}
            style={{ width: '100%' }}
            defaultValue={val != null ? String(val) : ''}
            onBlur={(e) => {
              const raw = e.target.value;
              const v = raw === '' ? undefined : Number(raw);
              if (v !== record.weight && (v === undefined || !Number.isNaN(v))) {
                updatePacking.mutate({ itemId: record.id, body: { weight: v } });
              }
            }}
          />
        ),
    },
    {
      title: 'HSN Code',
      dataIndex: 'hsn_code',
      key: 'hsn_code',
      width: 120,
      render: (val: string, record: DeliveryNoteItemShape) =>
        packingLocked ? (
          <span>{val ?? '—'}</span>
        ) : (
          <Input
            size="small"
            defaultValue={val ?? ''}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (record.hsn_code ?? '')) {
                updatePacking.mutate({ itemId: record.id, body: { hsn_code: v || undefined } });
              }
            }}
          />
        ),
    },
  ];

  const packageColumns: ColumnsType<PackageDraftRow> = [
    {
      title: '#',
      key: 'idx',
      width: 50,
      render: (_v, _r, index) => index + 1,
    },
    {
      title: 'Type',
      dataIndex: 'package_type',
      key: 'package_type',
      width: 110,
      render: (val: string, record) =>
        packingLocked ? (
          <span>{val === 'pallet' ? 'Pallet' : 'Box'}</span>
        ) : (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={val || 'box'}
            options={[
              { value: 'box', label: 'Box' },
              { value: 'pallet', label: 'Pallet' },
            ]}
            onChange={(v) =>
              setPackageDrafts((rows) =>
                rows.map((r) => (r.key === record.key ? { ...r, package_type: v } : r))
              )
            }
          />
        ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      render: (val: number, record) =>
        packingLocked ? (
          <span>{val}</span>
        ) : (
          <Input
            type="number"
            size="small"
            min={1}
            step={1}
            style={{ width: '100%' }}
            value={val}
            onChange={(e) => {
              const n = Number(e.target.value);
              setPackageDrafts((rows) =>
                rows.map((r) =>
                  r.key === record.key
                    ? { ...r, quantity: Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1 }
                    : r
                )
              );
            }}
          />
        ),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (val: string, record) =>
        packingLocked ? (
          <span>{val || '—'}</span>
        ) : (
          <Input
            size="small"
            value={val ?? ''}
            onChange={(e) =>
              setPackageDrafts((rows) =>
                rows.map((r) => (r.key === record.key ? { ...r, size: e.target.value } : r))
              )
            }
          />
        ),
    },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (val: number | undefined, record) =>
        packingLocked ? (
          <span>{val != null ? String(val) : '—'}</span>
        ) : (
          <Input
            type="number"
            size="small"
            min={0}
            style={{ width: '100%' }}
            value={val != null ? String(val) : ''}
            onChange={(e) => {
              const raw = e.target.value;
              setPackageDrafts((rows) =>
                rows.map((r) =>
                  r.key === record.key
                    ? { ...r, weight: raw === '' ? undefined : Number(raw) }
                    : r
                )
              );
            }}
          />
        ),
    },
    {
      title: 'UOM',
      dataIndex: 'weight_uom',
      key: 'weight_uom',
      width: 80,
      render: (val: string, record) =>
        packingLocked ? (
          <span>{val || 'KG'}</span>
        ) : (
          <Input
            size="small"
            value={val ?? 'KG'}
            onChange={(e) =>
              setPackageDrafts((rows) =>
                rows.map((r) => (r.key === record.key ? { ...r, weight_uom: e.target.value } : r))
              )
            }
          />
        ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (val: string, record) =>
        packingLocked ? (
          <span>{val || '—'}</span>
        ) : (
          <Input
            size="small"
            value={val ?? ''}
            onChange={(e) =>
              setPackageDrafts((rows) =>
                rows.map((r) => (r.key === record.key ? { ...r, remarks: e.target.value } : r))
              )
            }
          />
        ),
    },
    ...(packingLocked
      ? []
      : [
          {
            title: '',
            key: 'actions',
            width: 48,
            render: (_: unknown, record: PackageDraftRow) => (
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => setPackageDrafts((rows) => rows.filter((r) => r.key !== record.key))}
              />
            ),
          } as ColumnsType<PackageDraftRow>[number],
        ]),
  ];

  const savePackages = () => {
    const payload: DeliveryPackageInput[] = packageDrafts.map((r) => ({
      package_type: r.package_type || 'box',
      quantity: r.quantity || 1,
      size: r.size || null,
      weight: r.weight != null && !Number.isNaN(Number(r.weight)) ? Number(r.weight) : null,
      weight_uom: r.weight_uom || 'KG',
      remarks: r.remarks || null,
    }));
    replacePackages.mutate(payload);
  };
  const statusColor = getDeliveryStatusTagColor(delivery.delivery_status);
  const st = delivery.delivery_status || 'Prepared';
  const soId = delivery.sales_order_id;

  const submitStage = (values: {
    remarks?: string;
    tracking_number?: string;
    shipping_method?: string;
    vehicle_number?: string;
    driver_name?: string;
    delivery_assignee_user_id?: string;
  }) => {
    if (!id || !stageModal) return;
    const opts = {
      onSuccess: () => {
        setStageModal(null);
        stageForm.resetFields();
      },
    };
    if (stageModal === 'in-transit') {
      const body = {
        ...(values.remarks?.trim() ? { remarks: values.remarks.trim() } : {}),
        tracking_number: (values.tracking_number ?? '').trim(),
        shipping_method: (values.shipping_method ?? '').trim(),
        vehicle_number: (values.vehicle_number ?? '').trim(),
        driver_name: (values.driver_name ?? '').trim(),
        delivery_assignee_user_id: values.delivery_assignee_user_id || '',
      };
      markInTransit.mutate({ id, body }, opts);
    } else {
      const body = {
        ...(values.remarks?.trim() ? { remarks: values.remarks.trim() } : {}),
        tracking_number: (values.tracking_number ?? '').trim(),
        shipping_method: (values.shipping_method ?? '').trim(),
      };
      markReturned.mutate({ id, body }, opts);
    }
  };

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/delivery-notes')}>
        Back
      </Button>
      {canTransition && (st === 'Prepared' || st === 'In Transit') ? (
        <Button
          type="default"
          icon={<EditOutlined />}
          loading={updateTransit.isPending}
          onClick={() => {
            transitForm.setFieldsValue({
              remarks: delivery.status_transition_remarks ?? '',
              tracking_number: delivery.tracking_number ?? '',
              shipping_method: delivery.shipping_method ?? '',
              vehicle_number: delivery.vehicle_number ?? '',
              driver_name: delivery.driver_name ?? '',
              delivery_assignee_user_id: delivery.delivery_assignee_user_id ?? undefined,
              in_transit_at: delivery.in_transit_at ? dayjs(delivery.in_transit_at) : null,
            });
            setTransitEditOpen(true);
          }}
        >
          Edit transit details
        </Button>
      ) : null}
      {canTransition && st === 'Prepared' ? (
        <Button type="default" loading={transitionBusy} onClick={() => setStageModal('in-transit')}>
          Mark in transit
        </Button>
      ) : null}
      {canTransition && st === 'In Transit' ? (
        <>
          <Button type="default" loading={transitionBusy} onClick={() => setStageModal('delivered')}>
            Mark delivered
          </Button>
          <Button danger type="default" loading={transitionBusy} onClick={() => setStageModal('returned')}>
            Mark returned
          </Button>
        </>
      ) : null}
      <Dropdown
        menu={{
          items: [
            {
              key: 'packing-letterhead',
              label: 'Packing list (with letterhead)',
              icon: <PrinterOutlined />,
              onClick: () =>
                navigate(`/print/sales/delivery-notes/${id}?variant=packing&format=letterhead`),
            },
            {
              key: 'packing-preprinted',
              label: 'Packing list (preprinted)',
              icon: <PrinterOutlined />,
              onClick: () =>
                navigate(`/print/sales/delivery-notes/${id}?variant=packing&format=preprinted`),
            },
            {
              key: 'dn-letterhead',
              label: 'Delivery note (with letterhead)',
              icon: <PrinterOutlined />,
              onClick: () =>
                navigate(
                  `/print/sales/delivery-notes/${id}?variant=delivery-note&format=letterhead`
                ),
            },
            {
              key: 'dn-preprinted',
              label: 'Delivery note (preprinted)',
              icon: <PrinterOutlined />,
              onClick: () =>
                navigate(
                  `/print/sales/delivery-notes/${id}?variant=delivery-note&format=preprinted`
                ),
            },
            {
              key: 'ci-letterhead',
              label: 'Commercial invoice (with letterhead)',
              icon: <PrinterOutlined />,
              onClick: () =>
                navigate(
                  `/print/sales/delivery-notes/${id}?variant=commercial-invoice&format=letterhead`
                ),
            },
            {
              key: 'ci-preprinted',
              label: 'Commercial invoice (preprinted)',
              icon: <PrinterOutlined />,
              onClick: () =>
                navigate(
                  `/print/sales/delivery-notes/${id}?variant=commercial-invoice&format=preprinted`
                ),
            },
            ...(delivery.stock_journal_voucher_id
              ? [
                  { type: 'divider' as const },
                  {
                    key: 'stock-voucher',
                    label: 'Print voucher',
                    icon: <PrinterOutlined />,
                    disabled: printingVoucher,
                    onClick: () => {
                      void handlePrintStockVoucher();
                    },
                  },
                ]
              : []),
          ] as MenuProps['items'],
        }}
        trigger={['click']}
      >
        <Button type="primary" icon={<PrinterOutlined />} loading={printingVoucher}>
          Print <DownOutlined />
        </Button>
      </Dropdown>
      <Select
        value={reportKey}
        onChange={setReportKey}
        options={DELIVERY_REPORT_VARIANTS}
        style={{ width: 170 }}
        aria-label="Document"
      />
      <ReportDocumentActions
        reportKey={reportKey}
        params={{ id: delivery.id }}
        documentId={delivery.id}
        sendPermissions={['growth.delivery.edit', 'growth.order.edit']}
        emailLabel="Email customer"
        allowPreprinted
      />
    </Space>
  );

  const openProofDocument = async (docId: string, mode: 'view' | 'download') => {
    try {
      const blob =
        mode === 'view'
          ? await documentService.viewDocument(docId)
          : await documentService.downloadDocument(docId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Unable to open proof file');
    }
  };

  const rightRail = (
    <div>
      <Title level={5}>Quick links</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/growth/delivery-notes')}>
          All delivery notes
        </Button>
        {soId && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate(`/growth/orders/${soId}`)}>
            Customer order
          </Button>
        )}
        {(delivery.sales_invoices ?? []).map((inv) => (
          <Button
            key={inv.id}
            type="link"
            block
            style={{ textAlign: 'left', paddingLeft: 0 }}
            onClick={() => navigate(`/growth/invoices/${inv.id}`)}
          >
            {inv.invoice_number}
          </Button>
        ))}
      </Space>
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0' }}>
      <Modal
        title={
          stageModal === 'in-transit'
            ? 'Mark in transit'
            : stageModal === 'returned'
                ? 'Mark returned'
                : 'Update status'
        }
        open={stageModal != null && stageModal !== 'delivered'}
        onCancel={() => {
          setStageModal(null);
          stageForm.resetFields();
        }}
        okText="Confirm"
        confirmLoading={transitionBusy}
        destroyOnClose
        onOk={() => stageForm.submit()}
      >
        <Form form={stageForm} layout="vertical" onFinish={submitStage}>
          <Form.Item label="Remarks (optional)" name="remarks">
            <Input.TextArea rows={3} placeholder="Carrier / recipient notes" />
          </Form.Item>
          <Form.Item label="Tracking number (optional)" name="tracking_number">
            <Input placeholder="Tracking / AWB" />
          </Form.Item>
          <Form.Item label="Shipping method (optional)" name="shipping_method">
            <Input placeholder="e.g. Courier, own vehicle" />
          </Form.Item>
          {stageModal === 'in-transit' ? (
            <>
              <Form.Item label="Vehicle number (optional)" name="vehicle_number">
                <Input placeholder="Vehicle / plate number" />
              </Form.Item>
              <Form.Item label="Driver name (optional)" name="driver_name">
                <Input placeholder="Driver on record" />
              </Form.Item>
              <Form.Item
                label="Delivery man"
                name="delivery_assignee_user_id"
                rules={[{ required: true, message: 'Select the user responsible for completing delivery' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={assigneeUserOptions}
                  loading={assigneeUsersLoading}
                  placeholder="Select user — they will be notified"
                  allowClear={false}
                />
              </Form.Item>
            </>
          ) : null}
        </Form>
      </Modal>
      <Modal
        title="Edit transit details"
        open={transitEditOpen}
        onCancel={() => {
          setTransitEditOpen(false);
          transitForm.resetFields();
        }}
        okText="Save"
        confirmLoading={updateTransit.isPending}
        destroyOnClose
        onOk={() => transitForm.submit()}
      >
        <Form
          form={transitForm}
          layout="vertical"
          onFinish={(values) => {
            if (!id) return;
            updateTransit.mutate(
              {
                id,
                body: {
                  remarks: (values.remarks ?? '').trim(),
                  tracking_number: (values.tracking_number ?? '').trim(),
                  shipping_method: (values.shipping_method ?? '').trim(),
                  vehicle_number: (values.vehicle_number ?? '').trim(),
                  driver_name: (values.driver_name ?? '').trim(),
                  delivery_assignee_user_id: values.delivery_assignee_user_id || '',
                  ...(values.in_transit_at
                    ? { in_transit_at: values.in_transit_at.toISOString() }
                    : st === 'In Transit'
                      ? {}
                      : { in_transit_at: '' }),
                },
              },
              {
                onSuccess: () => {
                  setTransitEditOpen(false);
                  transitForm.resetFields();
                },
              }
            );
          }}
        >
          <Form.Item label="Shipping method" name="shipping_method">
            <Input placeholder="e.g. Road, Courier" />
          </Form.Item>
          <Form.Item label="Tracking number" name="tracking_number">
            <Input placeholder="Tracking / AWB" />
          </Form.Item>
          <Form.Item label="Vehicle number" name="vehicle_number">
            <Input placeholder="Vehicle / plate number" />
          </Form.Item>
          <Form.Item label="Driver name" name="driver_name">
            <Input placeholder="Driver on record" />
          </Form.Item>
          <Form.Item label="Delivery man" name="delivery_assignee_user_id">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={assigneeUserOptions}
              loading={assigneeUsersLoading}
              placeholder="Select delivery user"
            />
          </Form.Item>
          <Form.Item
            label="In transit at"
            name="in_transit_at"
            rules={
              st === 'In Transit'
                ? [{ required: true, message: 'In-transit time is required' }]
                : undefined
            }
          >
            <DatePicker showTime format="DD-MM-YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Status remarks" name="remarks">
            <Input.TextArea rows={3} placeholder="Carrier / recipient notes" />
          </Form.Item>
        </Form>
      </Modal>
      <DeliveryCompletionModal
        open={stageModal === 'delivered'}
        deliveryId={delivery.id}
        deliveryNumber={delivery.delivery_note_number}
        customerName={(delivery as { customer?: { customer_name?: string } }).customer?.customer_name}
        deliveryAddress={delivery.delivery_address ?? null}
        instructions={delivery.special_instructions ?? null}
        onClose={() => setStageModal(null)}
      />
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Delivery note"
            documentNumber={delivery.delivery_note_number}
            statusTag={
              <Space size={4}>
                <Tag color={statusColor}>{delivery.delivery_status || 'Prepared'}</Tag>
                {delivery.stock_posted ? (
                  <Tag color="green">Stock posted</Tag>
                ) : delivery.delivery_status === 'Delivered' ? (
                  <Tag color="orange">Stock not posted</Tag>
                ) : null}
              </Space>
            }
            subtitle={
              <Text type="secondary">
                {delivery.delivery_date ? dayjs(delivery.delivery_date).format('DD MMM YYYY') : ''}
              </Text>
            }
            facts={
              <Space wrap>
                <Text>
                  <strong>Customer:</strong> {(delivery as { customer?: { customer_name?: string } }).customer?.customer_name ?? '—'}
                </Text>
                <Text>
                  <strong>SO:</strong>{' '}
                  {(delivery as { salesOrder?: { sales_order_number?: string } }).salesOrder?.sales_order_number ?? '—'}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={dnWorkflowStages} current={dnWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        <TaskRoutingCard
          sourceDocumentType="DeliveryNote"
          sourceDocumentId={delivery.id}
          nextActionUserId={(delivery as { next_action_user_id?: string | null }).next_action_user_id}
          documentOwnerLabel="Delivery assignee"
          documentOwnerUserId={delivery.delivery_assignee_user_id}
          disabled={['Delivered', 'Returned', 'Cancelled'].includes(delivery.delivery_status || '')}
          invalidateQueryKeys={[['deliveries', id]]}
        />
        <Collapse
          defaultActiveKey={['hdr', 'lines', 'docs', 'irn', 'proof']}
          items={[
            {
              key: 'hdr',
              label: 'Shipment details',
              children: (
                <div>
                  <Card bordered={false} styles={{ body: { padding: 0 } }}>
                    <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
                      <Descriptions.Item label="Revenue service type">{dnRstLabel}</Descriptions.Item>
                      <Descriptions.Item label="Customer Order">
                        {(delivery as { salesOrder?: { sales_order_number?: string } }).salesOrder?.sales_order_number ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Tax invoice(s)">
                        {(() => {
                          const invoices = delivery.sales_invoices?.length
                            ? delivery.sales_invoices
                            : delivery.sales_invoice_number
                              ? [{ id: '', invoice_number: delivery.sales_invoice_number }]
                              : [];
                          if (!invoices.length) return '—';
                          return (
                            <Space size={[4, 4]} wrap>
                              {invoices.map((inv, idx) => (
                                <React.Fragment key={inv.id || inv.invoice_number}>
                                  {idx > 0 ? <span>,</span> : null}
                                  {inv.id ? (
                                    <Button
                                      type="link"
                                      size="small"
                                      style={{ padding: 0, height: 'auto' }}
                                      onClick={() => navigate(`/growth/invoices/${inv.id}`)}
                                    >
                                      {inv.invoice_number}
                                    </Button>
                                  ) : (
                                    <span>{inv.invoice_number}</span>
                                  )}
                                </React.Fragment>
                              ))}
                            </Space>
                          );
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Customer">
                        {(delivery as { customer?: { customer_name?: string } }).customer?.customer_name ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="L.P.O">{delivery.customer_po_number ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="Delivery Date">
                        {delivery.delivery_date ? dayjs(delivery.delivery_date).format('DD MMM YYYY') : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">{delivery.delivery_status}</Descriptions.Item>
                      <Descriptions.Item label="Source warehouse" span={2}>
                        {packingLocked || delivery.stock_posted ? (
                          warehouseLabel
                        ) : (
                          <Select
                            showSearch
                            optionFilterProp="label"
                            style={{ minWidth: 260 }}
                            placeholder="Select warehouse"
                            value={delivery.warehouse_id || undefined}
                            onChange={(v) => updateWarehouse.mutate(v)}
                            options={warehouses
                              .filter((w) => w.is_active !== false)
                              .map((w) => ({
                                value: w.id,
                                label: `${w.warehouse_code} — ${w.warehouse_name}`,
                              }))}
                          />
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Stock posted">
                        {delivery.stock_posted
                          ? delivery.stock_posted_at
                            ? dayjs(delivery.stock_posted_at).format('DD MMM YYYY HH:mm')
                            : 'Yes'
                          : 'No'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Prepared By">{preparedByUsername}</Descriptions.Item>
                      <Descriptions.Item label="Tracking">{delivery.tracking_number ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="Shipping method">{delivery.shipping_method ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="Vehicle no.">{delivery.vehicle_number ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="Driver name">{delivery.driver_name ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="Delivery man" span={2}>
                        {delivery.deliveryAssignee
                          ? `${delivery.deliveryAssignee.first_name}${delivery.deliveryAssignee.last_name ? ` ${delivery.deliveryAssignee.last_name}` : ''}`
                          : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="In transit at">
                        {delivery.in_transit_at ? dayjs(delivery.in_transit_at).format('DD MMM YYYY HH:mm') : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Delivered at">
                        {delivery.delivered_complete_at
                          ? dayjs(delivery.delivered_complete_at).format('DD MMM YYYY HH:mm')
                          : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Returned at">
                        {delivery.returned_at ? dayjs(delivery.returned_at).format('DD MMM YYYY HH:mm') : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status remarks" span={3}>
                        {delivery.status_transition_remarks ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Address" span={3}>
                        {delivery.delivery_address ?? '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Country of Origin" span={3}>
                        {packingLocked ? (
                          delivery.country_of_origin || '—'
                        ) : (
                          <Input
                            style={{ maxWidth: 280 }}
                            defaultValue={delivery.country_of_origin ?? ''}
                            placeholder="e.g. UAE"
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (delivery.country_of_origin ?? '')) {
                                updateCountryOfOrigin.mutate(v || null);
                              }
                            }}
                          />
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </div>
              ),
            },
            {
              key: 'lines',
              label: 'Line items & packing',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={items}
                    pagination={false}
                    size="small"
                    scroll={{ x: 900 }}
                  />
                  <Card
                    size="small"
                    title="Boxes / Pallets"
                    extra={
                      packingLocked ? null : (
                        <Space>
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() =>
                              setPackageDrafts((rows) => [
                                ...rows,
                                {
                                  key: `new-${Date.now()}`,
                                  package_type: 'box',
                                  quantity: 1,
                                  size: '',
                                  weight: undefined,
                                  weight_uom: 'KG',
                                  remarks: '',
                                },
                              ])
                            }
                          >
                            Add row
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            loading={replacePackages.isPending}
                            onClick={savePackages}
                          >
                            Save packages
                          </Button>
                        </Space>
                      )
                    }
                  >
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Record shipping boxes or pallets with size and weight (separate from line items).
                    </Text>
                    <Table
                      rowKey="key"
                      columns={packageColumns}
                      dataSource={packageDrafts}
                      pagination={false}
                      size="small"
                      locale={{ emptyText: 'No boxes or pallets recorded yet' }}
                      scroll={{ x: 800 }}
                    />
                  </Card>
                </Space>
              ),
            },
            {
              key: 'docs',
              label: `Client document package (${approvedPackage.length})`,
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  <Space wrap>
                    <Text>
                      <strong>Package status:</strong>{' '}
                      <Tag
                        color={
                          delivery.document_package_status === 'Approved'
                            ? 'green'
                            : delivery.document_package_status === 'Rejected'
                              ? 'red'
                              : 'gold'
                        }
                      >
                        {delivery.document_package_status || 'PendingCollection'}
                      </Tag>
                    </Text>
                    {delivery.document_collection_request_id ? (
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          navigate(`/growth/document-collections/${delivery.document_collection_request_id}`)
                        }
                      >
                        Open document collection
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        size="small"
                        loading={createDocumentCollection.isPending}
                        onClick={() =>
                          createDocumentCollection.mutate(undefined, {
                            onSuccess: (dcr) => {
                              message.success('Document collection created');
                              navigate(`/growth/document-collections/${dcr.id}`);
                            },
                            onError: (e: Error) => message.error(e.message || 'Failed to create document collection'),
                          })
                        }
                      >
                        Create document collection
                      </Button>
                    )}
                  </Space>
                  {approvedPackage.length === 0 ? (
                    <Text type="secondary">No approved package documents yet.</Text>
                  ) : (
                    approvedPackage.map((pkg) => (
                      <Card key={pkg.id} size="small">
                        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                          <Space direction="vertical" size={0}>
                            <Text strong>{pkg.document?.document_name || pkg.document?.file_name}</Text>
                            <Text type="secondary">{pkg.category}</Text>
                          </Space>
                          {pkg.document?.id && (
                            <Button size="small" onClick={() => documentService.viewDocument(pkg.document!.id)}>
                              View
                            </Button>
                          )}
                        </Space>
                      </Card>
                    ))
                  )}
                </Space>
              ),
            },
            {
              key: 'irn',
              label: 'Inspection Release Note (IRN)',
              children: id ? (
                <div>
                  <InspectionReleaseNoteComponent deliveryNoteId={id} />
                </div>
              ) : null,
            },
            {
              key: 'proof',
              label: `Proof of delivery (${proofDocs.length})`,
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  <ProofOfDeliveryUpload
                    deliveryId={delivery.id}
                    deliveryNumber={delivery.delivery_note_number}
                    disabled={st === 'Returned'}
                  />
                  {proofDocs.length === 0 ? (
                    <Text type="secondary">No proof uploaded yet.</Text>
                  ) : (
                    proofDocs.map((doc) => (
                      <Card key={doc.id} size="small">
                        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                          <Space direction="vertical" size={0}>
                            <Text strong>{doc.document_name || doc.file_name}</Text>
                            <Text type="secondary">
                              {doc.created_at ? dayjs(doc.created_at).format('DD MMM YYYY HH:mm') : '-'}
                            </Text>
                          </Space>
                          <Space>
                            <Button size="small" onClick={() => openProofDocument(doc.id, 'view')}>
                              View
                            </Button>
                            <Button size="small" onClick={() => openProofDocument(doc.id, 'download')}>
                              Download
                            </Button>
                          </Space>
                        </Space>
                      </Card>
                    ))
                  )}
                </Space>
              ),
            },
            {
              key: 'earlier-documents',
              label: 'From earlier documents',
              children: (
                <InheritedAttachmentsCard
                  trailDomain="sales"
                  entityType="DeliveryNote"
                  entityId={delivery.id}
                  emptyText="No files on the customer order, quotation or opportunity."
                  style={{ marginTop: 0 }}
                />
              ),
            },
          ]}
        />
      </WorkspaceLayout>
    </div>
  );
};

export default DeliveryDetailPage;
